import { formatGaslessRelayError } from "./gaslessRelayError";
import { formatParsedContractError } from "../../../utils/errors.decode";
import { MARKET_ORDER_MESSAGES } from "../../../utils/errors.constants";
import { orderRejected, toOrderTransactionError } from "./orderTransactionError";
import {
  buildSpotMarketBounds,
  checkSpotLockSolvency,
  namedMarketInRevert,
  quoteSpotMarketOrder,
  readSpotTakerFee,
  SpotMarketBounds,
} from "./useSpotOrder.bounds";
import { extractErrorDataCandidates } from "../../../utils/errors.extract";
import { toTokenUnits } from "../../../utils/decimalString";
import {
  parseRelayerResponse,
  parseRelayerResponseSoft,
} from "./relayerResponse";
import { MutableRefObject } from "react";
import { ethers } from "ethers";
import { getWeb3ProviderForChain } from "../../../utils/wallet/activeSigner";
import { OrderTypeResponse, SideResponse } from "../../../codegen-api";
import { getCurrentChainConfig } from "../../../config/chains";
import { getSpotDomainName } from "../../../utils/gaslessDomains";
import { InviteStatusResponse } from "../invite/useInviteStatus";
import { ISpotOrderParams, SpotCallArgs } from "./useSpotOrder.types";
import {
  resolveSpotSigner,
  ERC20_DECIMALS_ABI,
  BALANCE_MANAGER_ABI,
  CLOB_ENGINE_ORDER_ABI,
  CLOB_ENGINE_ORDER_ABI_V2,
  ERC20_APPROVE_ABI,
  PLACE_MARKET_ORDER_TYPES,
  PLACE_MARKET_ORDER_TYPES_V2,
  PLACE_SPOT_ORDER_TYPES,
} from "./useSpotOrder.helpers";

const HOOK_REJECTED_WITHDRAWAL_SELECTOR = "0x871d2813";

/** True when the chain refused with the argument-less HookRejectedWithdrawal(). */
const isHookRejectedWithdrawal = (error: unknown): boolean =>
  extractErrorDataCandidates(error).some((candidate) =>
    String(candidate).toLowerCase().startsWith(HOOK_REJECTED_WITHDRAWAL_SELECTOR)
  );

export interface IPlaceSpotOrderCtx {
  account?: string | null;
  masterAccount?: string | null;
  inviteStatus: InviteStatusResponse | undefined;
  /** The trader's max-slippage setting, basis points; bounds a market order. */
  slippageBps: number;
  walletTransactionInFlightRef: MutableRefObject<boolean>;
  setIsLoading: (value: boolean) => void;
  revalidateOrdersSoon: () => void;
}

export async function placeSpotOrderImpl(
  params: ISpotOrderParams,
  ctx: IPlaceSpotOrderCtx
) {
  const {
    account,
    masterAccount,
    inviteStatus,
    slippageBps,
    walletTransactionInFlightRef,
    setIsLoading,
    revalidateOrdersSoon,
  } = ctx;
  if (!account) throw new Error("Not connected");
  // Subaccounts are perp-only on-chain (CLOBEngine has no subaccount authorization).
  if (masterAccount && account.toLowerCase() !== masterAccount.toLowerCase()) {
    throw new Error("Switch to your main account to trade spot — subaccounts are perp-only.");
  }
  if (!inviteStatus) {
    throw new Error("Checking invite access. Please try again in a moment.");
  }
  if (inviteStatus?.invite_required && !inviteStatus.allowed) {
    throw new Error("Invite code required to trade");
  }
  if (walletTransactionInFlightRef.current) {
    throw new Error(
      "Transaction already in progress. Please confirm or reject the open wallet request."
    );
  }

  const config = getCurrentChainConfig();
  const hasRouter = !!config.contracts.perpetualDex?.router;

  // Ultrafuzz M-1: on modular clob-dex chains that set this flag (Arc), the CLOBRouter market-order
  // functions carry a trailing `maxQuoteIn` (both the direct placeMarketOrder[WithDeposit] and the
  // signed placeMarketOrderBySignature). Drives the 6-param direct ABI/args below and the V2 signed
  // struct. Monad (10143) shares only the signed V2 typehash — its consolidated CLOBEngine has a
  // different 7-param direct signature — so its gasless path is OR'd in by chainId, and its direct
  // path is intentionally left on the legacy ABI (untouched by this change).
  const marketHasMaxQuoteIn = config.spotMarketMaxQuoteIn === true;

  // On chains with a CLOBEngine/CLOBRouter, place orders directly from user's wallet
  if (hasRouter && params.base_currency && params.quote_currency) {
    walletTransactionInFlightRef.current = true;
    setIsLoading(true);
    // Declared outside the try so the catch can see it: the preflight learns WHY
    // the engine will refuse, but only the chain's argument-less refusal ever
    // reaches the trader.
    let riskEngineNote: string | undefined;
    try {
      const provider = await getWeb3ProviderForChain(config);
      const signer = provider.getSigner();
      const routerAddress = config.contracts.perpetualDex?.router;
      if (!routerAddress) throw new Error("Router address not configured");
      const balanceManagerAddress = config.contracts.perpetualDex?.balanceManager;
      if (!balanceManagerAddress) throw new Error("BalanceManager address not configured");

      const engine = new ethers.Contract(
        routerAddress,
        marketHasMaxQuoteIn ? CLOB_ENGINE_ORDER_ABI_V2 : CLOB_ENGINE_ORDER_ABI,
        signer
      );

      const pool = {
        baseCurrency: params.base_currency,
        quoteCurrency: params.quote_currency,
        orderBook: params.instrument_id,
      };

      const isBuy = params.side === SideResponse.Buy;
      const side = isBuy ? 0 : 1;
      const isMarket = params.order_type === OrderTypeResponse.Market;
      let signPrice = ethers.BigNumber.from(0); // raw limit price, for the gasless signature
      const baseToken = new ethers.Contract(params.base_currency, ERC20_DECIMALS_ABI, provider);
      const quoteToken = new ethers.Contract(params.quote_currency, ERC20_DECIMALS_ABI, provider);
      const [baseDecimals, quoteDecimals] = await Promise.all([
        baseToken.decimals(),
        quoteToken.decimals(),
      ]);
      if (!params.amount || Number(params.amount) <= 0) {
        throw new Error(
          "Order amount is zero — it may be below this market's lot size. Increase the amount (or enter it in base-asset terms)."
        );
      }
      // Token units, floored to the token's decimals. `parseUnits` threw on
      // exponent text ("5e-7") and on more places than the token has.
      const rawAmount = toTokenUnits(params.amount, baseDecimals);
      if (!rawAmount) {
        throw new Error(`Invalid order amount "${params.amount}".`);
      }
      if (rawAmount.lte(0)) {
        throw new Error(
          "Order amount rounds to zero at this market's lot size. Increase the amount."
        );
      }

      let tx: ethers.ContractTransaction;
      let callArgs: SpotCallArgs;
      let withDepositMethod: "placeMarketOrderWithDeposit" | "placeOrderWithDeposit";
      let normalMethod: "placeMarketOrder" | "placeOrder";
      let requiredLockAmount: ethers.BigNumber;
      // A market order's price bounds (see useSpotOrder.bounds); unset for a limit.
      let marketBounds: SpotMarketBounds | undefined;
      const lockCurrency = isBuy ? params.quote_currency : params.base_currency;
      const lockDecimals = isBuy ? quoteDecimals : baseDecimals;

      if (params.order_type === OrderTypeResponse.Market) {
        normalMethod = "placeMarketOrder";
        withDepositMethod = "placeMarketOrderWithDeposit";
        // Bound the order by the trader's max slippage. It used to send
        // minAmountOut = 0 and maxQuoteIn = uint256.max: "fill me at any
        // price". Both reads happen before anything is signed, and either one
        // failing stops the order rather than sending it unbounded.
        const [grossQuote, takerFee] = await Promise.all([
          quoteSpotMarketOrder(provider, params.instrument_id, rawAmount, side),
          readSpotTakerFee(provider, balanceManagerAddress),
        ]);
        marketBounds = buildSpotMarketBounds({
          isBuy,
          qtyRaw: rawAmount.toBigInt(),
          grossQuoteRaw: grossQuote.toBigInt(),
          slippageBps,
          fee: takerFee,
        });
        // On M-1 chains the market functions take a trailing `maxQuoteIn` (6 args); a SELL
        // ignores it. On the others the 5-arg form carries only minAmountOut, so a BUY's price
        // protection there is the lock, which tracks the book cost.
        callArgs = marketHasMaxQuoteIn
          ? ([
              [pool.baseCurrency, pool.quoteCurrency, pool.orderBook],
              rawAmount,
              side,
              account,
              marketBounds.minAmountOut,
              marketBounds.maxQuoteIn,
            ] as const)
          : ([
              [pool.baseCurrency, pool.quoteCurrency, pool.orderBook],
              rawAmount,
              side,
              account,
              marketBounds.minAmountOut,
            ] as const);
        // A BUY locks the book cost in quote; a SELL locks the base it sells.
        requiredLockAmount = isBuy ? grossQuote : rawAmount;
      } else {
        if (!params.price) throw new Error("Price required for limit orders");
        const rawPrice = toTokenUnits(params.price, quoteDecimals);
        if (!rawPrice || rawPrice.lte(0)) {
          throw new Error(`Invalid limit price "${params.price}".`);
        }
        signPrice = rawPrice;
        normalMethod = "placeOrder";
        withDepositMethod = "placeOrderWithDeposit";
        callArgs = [
          [pool.baseCurrency, pool.quoteCurrency, pool.orderBook],
          rawPrice,
          rawAmount,
          side,
          account,
        ] as const;
        requiredLockAmount = isBuy
          ? rawAmount
              .mul(rawPrice)
              .add(ethers.BigNumber.from(10).pow(baseDecimals).sub(1))
              .div(ethers.BigNumber.from(10).pow(baseDecimals))
          : rawAmount;
      }

      const balanceManager = new ethers.Contract(
        balanceManagerAddress,
        BALANCE_MANAGER_ABI,
        provider
      );
      const balanceManagerBalance: ethers.BigNumber =
        await balanceManager.getBalance(account, lockCurrency);
      const balanceManagerHasEnough =
        balanceManagerBalance.gte(requiredLockAmount);

      if (balanceManagerHasEnough) {
        // B5: spot balances and perp margin share one vault, so the risk engine
        // refuses to lock collateral that backs an open perp position
        // (HookRejectedWithdrawal). Ask it first and say so in plain words; only
        // a definite "no" stops the order — an unreadable answer lets the chain
        // decide, which still refuses safely.
        const solvency = await checkSpotLockSolvency(
          provider,
          config.contracts.perpetualDex?.quantumRiskEngine,
          account,
          lockCurrency,
          requiredLockAmount
        );
        // Only a definite "no" stops the order; anything else lets the chain
        // decide. But when the engine refused to answer AND named the market it
        // could not price, remember that -- the chain's own refusal carries no
        // arguments, so this is the only explanation anyone gets.
        const unpriceableMarket = namedMarketInRevert(
          solvency.revertData,
          config.contracts.perpetualDex?.markets
        );
        if (solvency.verdict === "insolvent") {
          throw orderRejected(MARKET_ORDER_MESSAGES.collateralBackingPerps);
        }
        if (unpriceableMarket) {
          riskEngineNote = MARKET_ORDER_MESSAGES.riskEngineCannotPriceMarket.replace(
            "{{market}}",
            unpriceableMarket
          );
        }

        // ── Gasless one-click: funds are already in the engine, so sign the order and relay it
        // (no wallet popup, no gas). One-click signs with the agent key; otherwise the connected
        // wallet signs once. The deposit-needed branch below stays a wallet flow (deposit must
        // be signed by the owner). ──
        if (config.gasless && config.relayerUrl) {
          const relayer = config.relayerUrl.replace(/\/+$/, "");
          const nonceRes = await fetch(`${relayer}/nonce/${account}?router=spot`);
          const nonce: string = String((await parseRelayerResponse(nonceRes, "nonce")).nonce);
          const deadline = String(Math.floor(Date.now() / 1000) + 3600);
          const ZERO = ethers.constants.AddressZero;
          const spotDomainName = getSpotDomainName(config);
          const domain = {
            name: spotDomainName,
            version: "1",
            chainId: config.chainId,
            verifyingContract: routerAddress,
          };
          const signWith: any = await resolveSpotSigner(
            account, config.chainId, routerAddress, spotDomainName, config.relayerUrl, provider, signer
          );
          let relayJson: any;
          let relayRes: Response;
          if (isMarket) {
            if (!marketBounds) throw new Error("INTERNAL: market order without bounds");
            // The signed market-order struct carries a `maxQuoteIn` field on Monad's CLOBEngine
            // (post-PR#18, chainId 10143) AND on modular clob-dex chains upgraded for Ultrafuzz M-1
            // (Arc and DogeOS, via spotMarketMaxQuoteIn). Chains without the flag (bnb) do not.
            const useMaxQuoteIn = config.chainId === 10143 || marketHasMaxQuoteIn;
            const minAmountOut = marketBounds.minAmountOut.toString();
            const maxQuoteIn = marketBounds.maxQuoteIn.toString();
            const types = useMaxQuoteIn ? PLACE_MARKET_ORDER_TYPES_V2 : PLACE_MARKET_ORDER_TYPES;
            const message: Record<string, unknown> = {
              user: account, orderBook: params.instrument_id, quantity: rawAmount, side,
              minAmountOut, builder: ZERO, builderFee: 0, nonce, deadline,
            };
            if (useMaxQuoteIn) message.maxQuoteIn = maxQuoteIn;
            const signature = await signWith._signTypedData(domain, types, message);
            relayRes = await fetch(`${relayer}/relay`, {
              method: "POST", headers: { "content-type": "application/json" },
              body: JSON.stringify({
                action: "placeMarketOrder",
                params: {
                  user: account, baseCurrency: params.base_currency, quoteCurrency: params.quote_currency,
                  orderBook: params.instrument_id, quantity: rawAmount.toString(), side,
                  minAmountOut, builder: ZERO, builderFee: 0, nonce, deadline,
                  ...(useMaxQuoteIn ? { maxQuoteIn } : {}),
                },
                signature,
              }),
            });
            relayJson = await parseRelayerResponseSoft(relayRes, "order relay");
          } else {
            const types = PLACE_SPOT_ORDER_TYPES;
            const message = {
              user: account, orderBook: params.instrument_id, price: signPrice, quantity: rawAmount,
              side, timeInForce: 0, builder: ZERO, builderFee: 0, nonce, deadline,
            };
            const signature = await signWith._signTypedData(domain, types, message);
            relayRes = await fetch(`${relayer}/relay`, {
              method: "POST", headers: { "content-type": "application/json" },
              body: JSON.stringify({
                action: "placeSpotOrder",
                params: {
                  user: account, baseCurrency: params.base_currency, quoteCurrency: params.quote_currency,
                  orderBook: params.instrument_id, price: signPrice.toString(), quantity: rawAmount.toString(),
                  side, timeInForce: 0, builder: ZERO, builderFee: 0, nonce, deadline,
                },
                signature,
              }),
            });
            relayJson = await parseRelayerResponseSoft(relayRes, "order relay");
          }
          // B4: an HTTP failure is a failure even when the body parses (the
          // cancel path already checked this), and relayed returndata is decoded
          // through the shared error table instead of surfacing as a selector.
          if (!relayRes.ok || relayJson?.success === false || !relayJson?.txHash) {
            throw orderRejected(
              formatGaslessRelayError(
                relayJson?.error,
                "Gasless order failed. Please try again.",
                {
                  reverted: relayJson?.reverted,
                  action: "order",
                  decodeRevertData: formatParsedContractError,
                }
              )
            );
          }
          revalidateOrdersSoon();
          return {
            order_id: relayJson.orderId?.toString() || relayJson.txHash,
            tx_hash: relayJson.txHash,
          };
        }

        const estimatedGas = await engine.estimateGas[normalMethod](...callArgs);
        const gasLimit = estimatedGas.mul(120).div(100);
        tx = await engine[normalMethod](...callArgs, { gasLimit });
      } else {
        const shortfall = requiredLockAmount.sub(balanceManagerBalance);
        const lockToken = new ethers.Contract(
          lockCurrency,
          [...ERC20_DECIMALS_ABI, ...ERC20_APPROVE_ABI],
          signer
        );
        const walletBalance: ethers.BigNumber = await lockToken.balanceOf(account);
        if (walletBalance.lt(shortfall)) {
          throw new Error(
            `Insufficient BalanceManager and wallet balance. Need ${ethers.utils.formatUnits(
              shortfall,
              lockDecimals
            )} more ${isBuy ? "quote" : "base"} asset.`
          );
        }

        const currentAllowance: ethers.BigNumber = await lockToken.allowance(
          account,
          balanceManagerAddress
        );
        if (currentAllowance.lt(shortfall)) {
          const approveTx = await lockToken.approve(
            balanceManagerAddress,
            ethers.constants.MaxUint256
          );
          await approveTx.wait(1);
        }

        const estimatedGas = await engine.estimateGas[withDepositMethod](...callArgs);
        const gasLimit = estimatedGas.mul(120).div(100);
        tx = await engine[withDepositMethod](...callArgs, { gasLimit });
      }

      const receipt = await tx.wait();
      revalidateOrdersSoon();

      return {
        order_id: receipt.logs?.[0]?.topics?.[1] || tx.hash,
        tx_hash: tx.hash,
      };
    } catch (error: any) {
      // `HookRejectedWithdrawal()` carries no arguments, so on its own it can
      // only produce a vague message. The preflight already asked the engine and
      // was told exactly which market it could not price -- prefer that.
      if (riskEngineNote && isHookRejectedWithdrawal(error)) {
        throw orderRejected(riskEngineNote);
      }
      // B4: a direct-transaction revert used to reach the trader as raw ethers
      // text or a bare selector. Decode it the way the perp path does; our own
      // worded errors pass through untouched.
      throw toOrderTransactionError(error, "Failed to place spot order");
    } finally {
      walletTransactionInFlightRef.current = false;
      setIsLoading(false);
    }
  }

  // Router not configured or missing market currency addresses
  throw new Error("Spot order placement requires base_currency and quote_currency from market data");
}
